/**
 * Take Payment by Phone — ESS-parity "Make a Payment" flow.
 *
 * Operator (Mary) calls a delinquent tenant, tenant reads card aloud, Mary
 * types it into Stripe Elements. Card PAN goes Browser -> Stripe direct;
 * mySMS360 backend never sees the number — only the `pm_...` token Stripe
 * issues in exchange.
 *
 * Three steps:
 *   1. Pick customer (or deep-link via ?customer_id=) + confirm amount.
 *   2. Stripe Elements CardElement + amount + Make Payment.
 *   3. Success / decline screen.
 *
 * Stripe Connect: Elements MUST be initialized against the facility's
 * connected account id (`stripeAccount` kwarg on loadStripe) so the
 * tokenized PaymentMethod lives on the right Stripe account — matches the
 * PortalPay tenant flow.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import {
  CreditCard, ChevronLeft, CheckCircle2, AlertCircle, Loader, Search,
} from 'lucide-react';

import apiClient from '../utils/apiClient';
import { useFacility } from '../context/FacilityContext';
import StripeTrustBadge from '../components/StripeTrustBadge';

const PUBLISHABLE_KEY = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '';

const fmtMoney = (n) => `$${(Number(n) || 0).toFixed(2)}`;
const fmtDate = (iso) => {
  if (!iso) return '—';
  const s = String(iso).slice(0, 10);
  const [y, m, d] = s.split('-');
  if (!y || !m || !d) return s;
  return `${parseInt(m, 10)}/${parseInt(d, 10)}/${y}`;
};

// ---------------------------------------------------------------------------
// Screen 1: Customer search + amount
// ---------------------------------------------------------------------------

const CustomerPicker = ({ facilityId, onSelect }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!facilityId) return;
    let cancelled = false;
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await apiClient.get(
          `/api/facilities/${facilityId}/customers/`,
          { params: query ? { search: query } : {} },
        );
        if (!cancelled) {
          // Backend returns array; client-side filter as belt-and-braces
          // if the endpoint ignores `search`.
          const q = query.toLowerCase();
          const filtered = q
            ? (res.data || []).filter((c) =>
                (c.full_name || '').toLowerCase().includes(q)
                || (c.email || '').toLowerCase().includes(q)
                || (c.phone || '').toLowerCase().includes(q)
              )
            : res.data || [];
          setResults(filtered.slice(0, 50));
        }
      } catch {
        if (!cancelled) setResults([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [query, facilityId]);

  return (
    <div className="rounded-xl border border-slate-200/80 bg-white p-6">
      <h2 className="text-[15px] font-semibold text-slate-900 mb-1">
        Select a customer to make a payment
      </h2>
      <p className="text-[13px] text-slate-500 mb-4">
        Search by name, email, or phone.
      </p>
      <div className="relative mb-3">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search customers…"
          autoFocus
          className="w-full pl-9 pr-3 py-2 rounded-lg border border-slate-200 text-[13px] focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>
      {loading ? (
        <p className="text-[13px] text-slate-400 py-4 text-center">Searching…</p>
      ) : (
        <ul className="divide-y divide-slate-100 max-h-96 overflow-auto">
          {results.length === 0 && (
            <li className="text-[13px] text-slate-400 py-4 text-center">
              No customers found.
            </li>
          )}
          {results.map((c) => (
            <li key={c._id}>
              <button
                onClick={() => onSelect(c._id)}
                className="w-full text-left py-2.5 px-2 hover:bg-slate-50 rounded transition-colors"
              >
                <div className="text-[13px] font-medium text-slate-900">
                  {c.full_name}
                </div>
                <div className="text-[12px] text-slate-500">
                  {c.email || '—'} · {c.phone || '—'}
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Items Due card — used on both Screen 1 (preview) and Screen 2 (line items)
// ---------------------------------------------------------------------------

const ItemsDueCard = ({ data, compact = false }) => {
  if (!data) return null;
  const hasInvoices = (data.invoices || []).length > 0;
  return (
    <div className="rounded-xl border border-slate-200/80 bg-white overflow-hidden">
      <div className="px-5 py-3 border-b border-slate-100">
        <h3 className="text-[14px] font-semibold text-slate-900">Items Due</h3>
        <p className="text-[12px] text-slate-500 mt-0.5">
          {data.customer?.full_name}
        </p>
      </div>
      {hasInvoices ? (
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
              <tr>
                <th className="text-left px-4 py-2">Invoice #</th>
                <th className="text-left px-4 py-2">Due</th>
                <th className="text-right px-4 py-2">Amount</th>
                <th className="text-right px-4 py-2">Tax</th>
                <th className="text-right px-4 py-2">Total</th>
              </tr>
            </thead>
            <tbody>
              {data.invoices.map((inv) => (
                <tr key={inv._id} className="border-t border-slate-100">
                  <td className="px-4 py-2 text-slate-700">
                    {inv.invoice_number || inv._id.slice(-6)}
                  </td>
                  <td className="px-4 py-2 text-slate-600">
                    {fmtDate(inv.due_date)}
                  </td>
                  <td className="px-4 py-2 text-right">{fmtMoney(inv.amount)}</td>
                  <td className="px-4 py-2 text-right">{fmtMoney(inv.tax_amount)}</td>
                  <td className="px-4 py-2 text-right font-medium">
                    {fmtMoney(inv.total)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="px-5 py-6 bg-blue-50 text-[13px] text-blue-800 text-center">
          Nothing due. Payment will add credit to the customer's account.
        </div>
      )}
      {!compact && (
        <div className="px-5 py-3 border-t border-slate-100 space-y-1 text-[13px]">
          <div className="flex justify-between text-slate-600">
            <span>Subtotal</span>
            <span>{fmtMoney(data.subtotal)}</span>
          </div>
          {data.tax > 0 && (
            <div className="flex justify-between text-slate-600">
              <span>Tax</span>
              <span>{fmtMoney(data.tax)}</span>
            </div>
          )}
          {data.late_fees > 0 && (
            <div className="flex justify-between text-slate-600">
              <span>Late fees</span>
              <span>{fmtMoney(data.late_fees)}</span>
            </div>
          )}
          {data.credit_applied > 0 && (
            <div className="flex justify-between text-emerald-700">
              <span>Customer credit</span>
              <span>−{fmtMoney(data.credit_applied)}</span>
            </div>
          )}
          <div className="flex justify-between text-[14px] font-semibold text-slate-900 pt-1 border-t border-slate-100 mt-2">
            <span>Amount Due</span>
            <span>{fmtMoney(data.amount_due)}</span>
          </div>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Screen 2: Stripe Elements + charge
// ---------------------------------------------------------------------------

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      fontSize: '14px',
      color: '#0f172a',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      '::placeholder': { color: '#94a3b8' },
    },
    invalid: { color: '#dc2626' },
  },
  hidePostalCode: false,
};

const PaymentForm = ({
  itemsDue, amountCents, setAmountCents, savedPmId, setSavedPmId,
  onResult, facilityId,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [cardholderName, setCardholderName] = useState(
    itemsDue?.customer?.full_name || ''
  );
  const [saveCard, setSaveCard] = useState(false);
  const [enableAutopay, setEnableAutopay] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const savedCards = itemsDue?.saved_cards || [];
  const usingSaved = !!savedPmId;
  const amountDollars = Number((amountCents / 100).toFixed(2));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (amountCents <= 0) {
      setError('Enter an amount greater than $0.');
      return;
    }
    setSubmitting(true);

    let paymentMethodId = null;
    if (!usingSaved) {
      if (!stripe || !elements) {
        setError('Stripe not loaded yet. Try again in a moment.');
        setSubmitting(false);
        return;
      }
      const card = elements.getElement(CardElement);
      if (!card) {
        setError('Card form not ready.');
        setSubmitting(false);
        return;
      }
      // Browser -> Stripe directly. PAN never reaches our backend.
      const { error: pmError, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card,
        billing_details: {
          name: cardholderName || itemsDue?.customer?.full_name,
          email: itemsDue?.customer?.email,
        },
      });
      if (pmError) {
        setError(pmError.message || 'Card validation failed.');
        setSubmitting(false);
        return;
      }
      paymentMethodId = paymentMethod.id;
    }

    try {
      const body = {
        customer_id: itemsDue.customer._id,
        amount_cents: amountCents,
        payment_method_id: usingSaved ? null : paymentMethodId,
        saved_payment_method_id: usingSaved ? savedPmId : null,
        save_card: !usingSaved && saveCard,
        // Autopay only meaningful if there's a card to autopay against:
        // either we're saving a new card, OR the operator picked a
        // saved card. The backend silently no-ops if neither applies.
        enable_autopay: enableAutopay && (usingSaved || saveCard),
        apply_to_invoice_ids: [],
      };
      const res = await apiClient.post(
        `/api/facilities/${facilityId}/operator-payments/charge`,
        body,
      );

      // 3DS challenge: confirm client-side, then surface final outcome.
      if (res.data.status === 'requires_action') {
        const { error: confirmError, paymentIntent } =
          await stripe.confirmCardPayment(res.data.client_secret);
        if (confirmError) {
          onResult({
            status: 'failed',
            decline_reason: confirmError.message || 'Authentication failed.',
            amount: amountDollars,
          });
          return;
        }
        // confirmCardPayment success — webhook will write the canonical row.
        // For UX: show success immediately; the backend GET path will
        // reconcile on next fetch.
        onResult({
          status: 'succeeded',
          amount: amountDollars,
          payment_intent_id: paymentIntent?.id,
          customer_email: itemsDue?.customer?.email,
          card_brand: null,
          card_last4: null,
          via_3ds: true,
        });
        return;
      }

      onResult(res.data);
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || 'Charge failed.';
      setError(typeof msg === 'string' ? msg : 'Charge failed.');
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-[12px] font-medium text-slate-600 mb-1">
          Payment Amount
        </label>
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-[13px]">$</span>
          <input
            type="number"
            min="0"
            step="0.01"
            value={(amountCents / 100).toFixed(2)}
            onChange={(e) => {
              const v = parseFloat(e.target.value || '0');
              setAmountCents(Math.round(Math.max(v, 0) * 100));
            }}
            className="w-full pl-7 pr-3 py-2 rounded-lg border border-slate-200 text-[13px] focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        {amountCents > Math.round(itemsDue.amount_due * 100) && itemsDue.amount_due > 0 && (
          <p className="mt-1.5 text-[12px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
            Adds {fmtMoney((amountCents / 100) - itemsDue.amount_due)} credit to customer's account.
          </p>
        )}
        {amountCents < Math.round(itemsDue.amount_due * 100) && (
          <p className="mt-1.5 text-[12px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
            Partial payment; {fmtMoney(itemsDue.amount_due - (amountCents / 100))} remains.
          </p>
        )}
      </div>

      <div>
        <label className="block text-[12px] font-medium text-slate-600 mb-1">
          Payment method
        </label>
        <select
          value={savedPmId || 'new'}
          onChange={(e) => setSavedPmId(e.target.value === 'new' ? null : e.target.value)}
          className="w-full px-3 py-2 rounded-lg border border-slate-200 text-[13px] focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          {savedCards.map((c) => (
            <option key={c.payment_method_id} value={c.payment_method_id}>
              Use saved card: {c.brand} ending {c.last4}
            </option>
          ))}
          <option value="new">One Time Credit Card</option>
        </select>
      </div>

      {!usingSaved && (
        <>
          <div>
            <label className="block text-[12px] font-medium text-slate-600 mb-1">
              Cardholder name
            </label>
            <input
              type="text"
              value={cardholderName}
              onChange={(e) => setCardholderName(e.target.value)}
              placeholder="Name on card"
              className="w-full px-3 py-2 rounded-lg border border-slate-200 text-[13px] focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-[12px] font-medium text-slate-600 mb-1">
              Card details
            </label>
            <div className="px-3 py-3 rounded-lg border border-slate-200 bg-white">
              <CardElement options={CARD_ELEMENT_OPTIONS} />
            </div>
            <StripeTrustBadge size="standard" />
          </div>
          <label className="flex items-center gap-2 text-[13px] text-slate-700 cursor-pointer">
            <input
              type="checkbox"
              checked={saveCard || enableAutopay}
              onChange={(e) => {
                const v = e.target.checked;
                setSaveCard(v);
                // Unchecking save_card also drops autopay (you can't
                // autopay against a card we didn't vault).
                if (!v) setEnableAutopay(false);
              }}
              className="rounded border-slate-300"
            />
            Save card on file for future payments
            {enableAutopay && (
              <span className="text-[11px] text-slate-400">(required for autopay)</span>
            )}
          </label>
          <label className="flex items-center gap-2 text-[13px] text-slate-700 cursor-pointer">
            <input
              type="checkbox"
              checked={enableAutopay}
              onChange={(e) => {
                const v = e.target.checked;
                setEnableAutopay(v);
                // Turning on autopay implies saving the card.
                if (v) setSaveCard(true);
              }}
              className="rounded border-slate-300"
            />
            Enable autopay (auto-charge this card monthly going forward)
          </label>
        </>
      )}
      {usingSaved && (
        <label className="flex items-center gap-2 text-[13px] text-slate-700 cursor-pointer">
          <input
            type="checkbox"
            checked={enableAutopay}
            onChange={(e) => setEnableAutopay(e.target.checked)}
            className="rounded border-slate-300"
          />
          Enable autopay (auto-charge this saved card monthly going forward)
        </label>
      )}

      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-[13px] text-red-700 flex items-start gap-2">
          <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <button
        type="submit"
        disabled={
          submitting
          || amountCents <= 0
          || (!usingSaved && (!stripe || !elements))
        }
        className="w-full rounded-lg bg-blue-600 text-white text-[13px] font-medium py-2.5 hover:bg-blue-700 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
      >
        {submitting ? (
          <><Loader className="h-4 w-4 animate-spin" /> Processing…</>
        ) : (
          <><CreditCard className="h-4 w-4" /> Make Payment {fmtMoney(amountCents / 100)}</>
        )}
      </button>
    </form>
  );
};

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

const TakePayment = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { facilityId } = useFacility();
  const initialCustomerId = params.get('customer_id');

  const [customerId, setCustomerId] = useState(initialCustomerId);
  const [itemsDue, setItemsDue] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Screen state machine: 'pick' | 'amount' | 'pay' | 'result'
  const [screen, setScreen] = useState(initialCustomerId ? 'amount' : 'pick');
  const [amountCents, setAmountCents] = useState(0);
  const [savedPmId, setSavedPmId] = useState(null);
  const [result, setResult] = useState(null);

  // Init Stripe against the FACILITY's connected account — same pattern as
  // PortalPay. The facility's stripe_account_id arrives in the items-due
  // payload (one network call covers both inventory + Stripe context).
  const stripePromise = useMemo(() => {
    const accountId = itemsDue?.facility?.stripe_account_id;
    if (!accountId || !PUBLISHABLE_KEY) return null;
    return loadStripe(PUBLISHABLE_KEY, { stripeAccount: accountId });
  }, [itemsDue?.facility?.stripe_account_id]);

  // Pull items due whenever the selected customer changes.
  useEffect(() => {
    if (!customerId || !facilityId) {
      setItemsDue(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const res = await apiClient.get(
          `/api/facilities/${facilityId}/operator-payments/customer/${customerId}/items-due`,
        );
        if (cancelled) return;
        setItemsDue(res.data);
        setAmountCents(Math.round((res.data.amount_due || 0) * 100));
        // Default to first saved card if any (operator can switch).
        const saved = res.data.saved_cards?.[0];
        setSavedPmId(saved ? saved.payment_method_id : null);
      } catch (err) {
        if (cancelled) return;
        const status = err.response?.status;
        if (status === 404) {
          setError('Customer not found in this facility.');
        } else {
          setError(err.response?.data?.detail || 'Failed to load payment info.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [customerId, facilityId]);

  // No publishable key configured at all → block early with a clear
  // message. Operator can't take card payments without it.
  if (!PUBLISHABLE_KEY) {
    return (
      <div className="px-6 py-8 max-w-2xl">
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-6">
          <h2 className="text-[15px] font-semibold text-amber-900 mb-1">
            Payments not configured
          </h2>
          <p className="text-[13px] text-amber-800">
            REACT_APP_STRIPE_PUBLISHABLE_KEY is not set. Phone payments are
            disabled until the Stripe publishable key is provisioned.
          </p>
        </div>
      </div>
    );
  }

  // -------- Screen: result --------
  if (screen === 'result' && result) {
    const succeeded = result.status === 'succeeded';
    return (
      <div className="px-6 py-8 max-w-2xl mx-auto">
        <div className={`rounded-xl border p-6 mb-4 ${succeeded
          ? 'border-emerald-200 bg-emerald-50'
          : 'border-red-200 bg-red-50'}`}>
          <div className="flex items-start gap-3">
            {succeeded ? (
              <CheckCircle2 className="h-6 w-6 text-emerald-600 flex-shrink-0" />
            ) : (
              <AlertCircle className="h-6 w-6 text-red-600 flex-shrink-0" />
            )}
            <div className="flex-1">
              {succeeded ? (
                <>
                  <h2 className="text-[15px] font-semibold text-emerald-900">
                    {fmtMoney(result.amount)} charged
                    {result.card_brand && result.card_last4
                      ? ` to ${result.card_brand} ending ${result.card_last4}`
                      : ''}
                  </h2>
                  <p className="text-[13px] text-emerald-800 mt-1">
                    Receipt {result.customer_email
                      ? `emailed to ${result.customer_email}`
                      : 'will be sent shortly'}.
                  </p>
                  {result.credited_to_customer > 0 && (
                    <p className="text-[12px] text-emerald-700 mt-1">
                      {fmtMoney(result.credited_to_customer)} added to
                      customer credit balance.
                    </p>
                  )}
                  {result.saved_for_future && (
                    <p className="text-[12px] text-emerald-700 mt-1">
                      Card saved on file for future payments.
                    </p>
                  )}
                </>
              ) : (
                <>
                  <h2 className="text-[15px] font-semibold text-red-900">
                    Payment failed
                  </h2>
                  <p className="text-[13px] text-red-800 mt-1">
                    {result.decline_reason || 'The card was declined.'}
                  </p>
                </>
              )}
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => navigate(`/customers/${customerId}/edit`)}
            className="px-4 py-2 rounded-lg bg-blue-600 text-white text-[13px] font-medium hover:bg-blue-700 transition-colors"
          >
            Return to Customer
          </button>
          {!succeeded && (
            <button
              onClick={() => {
                setResult(null);
                setScreen('pay');
              }}
              className="px-4 py-2 rounded-lg bg-blue-600 text-[13px] font-medium text-white hover:bg-blue-700 transition-colors"
            >
              Try again
            </button>
          )}
        </div>
      </div>
    );
  }

  // -------- Screen: customer picker --------
  if (screen === 'pick') {
    return (
      <div className="px-6 py-8 max-w-3xl mx-auto">
        <h1 className="text-[18px] font-semibold text-slate-900 mb-4 flex items-center gap-2">
          <CreditCard className="h-5 w-5 text-slate-500" /> Take Payment
        </h1>
        <CustomerPicker
          facilityId={facilityId}
          onSelect={(id) => {
            setCustomerId(id);
            setScreen('amount');
          }}
        />
      </div>
    );
  }

  // -------- Loading state --------
  if (loading) {
    return (
      <div className="px-6 py-8 flex items-center justify-center text-slate-400">
        <Loader className="h-5 w-5 animate-spin mr-2" /> Loading…
      </div>
    );
  }

  // -------- Error --------
  if (error) {
    return (
      <div className="px-6 py-8 max-w-2xl mx-auto">
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-[13px] text-red-700">
          {error}
        </div>
        <button
          onClick={() => navigate(-1)}
          className="mt-4 px-4 py-2 rounded-lg border border-slate-200 bg-white text-[13px] text-slate-700 hover:bg-slate-50"
        >
          Go back
        </button>
      </div>
    );
  }

  if (!itemsDue) return null;

  // -------- Screen: amount --------
  if (screen === 'amount') {
    return (
      <div className="px-6 py-8 max-w-3xl mx-auto">
        <button
          onClick={() => {
            if (initialCustomerId) navigate(-1);
            else setScreen('pick');
          }}
          className="text-[13px] text-slate-500 hover:text-slate-900 mb-4 flex items-center gap-1"
        >
          <ChevronLeft className="h-4 w-4" /> Back
        </button>
        <h1 className="text-[18px] font-semibold text-slate-900 mb-1 flex items-center gap-2">
          <CreditCard className="h-5 w-5 text-slate-500" /> Take Payment
        </h1>
        <p className="text-[13px] text-slate-500 mb-5">
          {itemsDue.customer.full_name} · {itemsDue.facility.name}
        </p>

        <div className="space-y-4">
          <ItemsDueCard data={itemsDue} />

          <div className="rounded-xl border border-slate-200/80 bg-white p-5">
            <label className="block text-[12px] font-medium text-slate-600 mb-1">
              Payment Amount
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-[13px]">$</span>
              <input
                type="number"
                min="0"
                step="0.01"
                value={(amountCents / 100).toFixed(2)}
                onChange={(e) => {
                  const v = parseFloat(e.target.value || '0');
                  setAmountCents(Math.round(Math.max(v, 0) * 100));
                }}
                className="w-full pl-7 pr-3 py-2 rounded-lg border border-slate-200 text-[14px] focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            {amountCents > Math.round(itemsDue.amount_due * 100) && itemsDue.amount_due > 0 && (
              <p className="mt-2 text-[12px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
                Adds {fmtMoney((amountCents / 100) - itemsDue.amount_due)} credit to customer's account.
              </p>
            )}
            {amountCents > 0
              && amountCents < Math.round(itemsDue.amount_due * 100) && (
              <p className="mt-2 text-[12px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
                Partial payment; {fmtMoney(itemsDue.amount_due - (amountCents / 100))} remains.
              </p>
            )}
            {itemsDue.amount_due === 0 && (
              <p className="mt-2 text-[12px] text-blue-700 bg-blue-50 border border-blue-200 rounded px-2 py-1.5">
                Nothing due. This payment will add credit to the customer's account.
              </p>
            )}
          </div>

          <button
            onClick={() => setScreen('pay')}
            disabled={amountCents <= 0}
            className="w-full rounded-lg bg-blue-600 text-white text-[14px] font-medium py-2.5 hover:bg-blue-700 disabled:opacity-50 transition-colors"
          >
            Proceed to Payment
          </button>
        </div>
      </div>
    );
  }

  // -------- Screen: pay (Stripe Elements) --------
  if (screen === 'pay') {
    if (!stripePromise) {
      return (
        <div className="px-6 py-8 max-w-2xl mx-auto">
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-[13px] text-amber-800">
            Facility has not completed Stripe Connect onboarding. Card payments
            require Stripe setup under Settings → Payments.
          </div>
        </div>
      );
    }
    return (
      <div className="px-6 py-8 max-w-3xl mx-auto">
        <button
          onClick={() => setScreen('amount')}
          className="text-[13px] text-slate-500 hover:text-slate-900 mb-4 flex items-center gap-1"
        >
          <ChevronLeft className="h-4 w-4" /> Back
        </button>
        <h1 className="text-[18px] font-semibold text-slate-900 mb-1 flex items-center gap-2">
          <CreditCard className="h-5 w-5 text-slate-500" /> Payment Summary
        </h1>
        <p className="text-[13px] text-slate-500 mb-5">
          {itemsDue.customer.full_name}
        </p>

        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div className="md:col-span-3 space-y-4">
            <ItemsDueCard data={itemsDue} />
          </div>
          <div className="md:col-span-2">
            <div className="rounded-xl border border-slate-200/80 bg-white p-5">
              <Elements stripe={stripePromise}>
                <PaymentForm
                  itemsDue={itemsDue}
                  amountCents={amountCents}
                  setAmountCents={setAmountCents}
                  savedPmId={savedPmId}
                  setSavedPmId={setSavedPmId}
                  facilityId={facilityId}
                  onResult={(r) => {
                    setResult(r);
                    setScreen('result');
                  }}
                />
              </Elements>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default TakePayment;
